import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import api from "../services/api";

// ─── SaaS Component Imports ───
import SaasNavbar from "../components/saas/SaasNavbar";
import SaasHero from "../components/saas/SaasHero";
import SaasTrust from "../components/saas/SaasTrust";
import SaasMultiTenant from "../components/saas/SaasMultiTenant";
import SaasSolutions from "../components/saas/SaasSolutions";
import SaasWhySkillzy from "../components/saas/SaasWhySkillzy";
import SaasDashboardPreview from "../components/saas/SaasDashboardPreview";
import SaasWhiteLabel from "../components/saas/SaasWhiteLabel";
import SaasAIFeatures from "../components/saas/SaasAIFeatures";
import SaasExamEcosystem from "../components/saas/SaasExamEcosystem";
import SaasSubjectiveExams from "../components/saas/SaasSubjectiveExams";
import SaasStudentExperience from "../components/saas/SaasStudentExperience";
import SaasAnalytics from "../components/saas/SaasAnalytics";
import SaasComparison from "../components/saas/SaasComparison";
import SaasTestimonials from "../components/saas/SaasTestimonials";
import SaasPricing from "../components/saas/SaasPricing";
import SaasFAQ from "../components/saas/SaasFAQ";
import SaasCTA from "../components/saas/SaasCTA";
import SaasFooter from "../components/saas/SaasFooter";

export default function SaasLandingPage() {
  const navigate = useNavigate();

  // ─── Demo Modal State ───
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    phone: "",
    institute: "",
    message: "",
  });

  // ─── SEO Meta Tags ───
  useEffect(() => {
    document.title =
      "Skillzy.pro — AI-Powered Multi-Tenant LMS for Schools, Colleges & Coaching";

    // Set meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      "Launch your institute's digital campus with Skillzy.pro. AI tutors, online & subjective exams, live classes, white-label LMS, multi-tenant architecture, and analytics — all in one platform.";

    // Set Open Graph tags
    const ogTags = {
      "og:title":
        "Skillzy.pro — AI-Powered Multi-Tenant LMS Platform",
      "og:description":
        "The complete SaaS LMS for schools, colleges & coaching institutes. AI exams, live classes, white-label branding, and 50,000+ students served.",
      "og:type": "website",
      "og:url": "https://skillzy.pro",
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = "smooth";

    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  // ─── Handlers ───
  const handleBookDemo = () => setShowDemoModal(true);

  const handleStartTrial = () => navigate("/register-institute");

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    try {
      await api.post("/saas/enquiries", demoForm);
      toast.success(
        "Demo request submitted! Our team will contact you within 24 hours."
      );
      setShowDemoModal(false);
      setDemoForm({ name: "", email: "", phone: "", institute: "", message: "" });
    } catch (err) {
      toast.error("Failed to submit demo request.");
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 font-['Inter',sans-serif] overflow-x-hidden">
      <Toaster position="top-center" />

      {/* ── Sticky Navbar ── */}
      <SaasNavbar onBookDemo={handleBookDemo} />

      {/* ── Hero Section ── */}
      <SaasHero onBookDemo={handleBookDemo} onStartTrial={handleStartTrial} />

      {/* ── Trust Metrics Bar ── */}
      <SaasTrust />

      {/* ── Multi-Tenant Architecture ── */}
      <SaasMultiTenant />

      {/* ── Tailored Solutions ── */}
      <SaasSolutions />

      {/* ── Why Skillzy (Feature Grid) ── */}
      <SaasWhySkillzy />

      {/* ── Dashboard Preview (Laptop Mockup) ── */}
      <SaasDashboardPreview />

      {/* ── White-Label Branding ── */}
      <SaasWhiteLabel />

      {/* ── AI Features ── */}
      <SaasAIFeatures />

      {/* ── Exam Ecosystem Workflow ── */}
      <SaasExamEcosystem />

      {/* ── Subjective Exams ── */}
      <SaasSubjectiveExams />

      {/* ── Student Mobile Experience ── */}
      <SaasStudentExperience />

      {/* ── Institutional Analytics ── */}
      <SaasAnalytics />

      {/* ── Comparison Table ── */}
      <SaasComparison />

      {/* ── Testimonials ── */}
      <SaasTestimonials />

      {/* ── Pricing Plans ── */}
      <SaasPricing />

      {/* ── FAQ Accordion ── */}
      <SaasFAQ />

      {/* ── Final CTA ── */}
      <SaasCTA onBookDemo={handleBookDemo} onStartTrial={handleStartTrial} />

      {/* ── Footer ── */}
      <SaasFooter />

      {/* ════════════════════════════════════════════
          BOOK DEMO MODAL
      ════════════════════════════════════════════ */}
      {showDemoModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-slate-950 rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden relative border border-slate-100 dark:border-slate-800"
          >
            {/* Close button */}
            <button
              onClick={() => setShowDemoModal(false)}
              className="absolute top-4 right-4 z-10 text-slate-400 hover:text-slate-600 dark:hover:text-white w-8 h-8 flex items-center justify-center bg-slate-100 dark:bg-slate-900 rounded-full font-bold transition-colors"
            >
              ✕
            </button>

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-violet-600 to-blue-600 p-8 text-center text-white">
              <h3 className="text-2xl font-black mb-2">Book a Free Demo</h3>
              <p className="text-violet-100 text-sm font-medium">
                See how Skillzy can transform your institute in 30 minutes.
              </p>
            </div>

            {/* Form */}
            <div className="p-8">
              <form onSubmit={handleDemoSubmit} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={demoForm.name}
                    onChange={(e) =>
                      setDemoForm({ ...demoForm, name: e.target.value })
                    }
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-medium text-sm text-slate-900 dark:text-white"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={demoForm.email}
                    onChange={(e) =>
                      setDemoForm({ ...demoForm, email: e.target.value })
                    }
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-medium text-sm text-slate-900 dark:text-white"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={demoForm.phone}
                    onChange={(e) =>
                      setDemoForm({ ...demoForm, phone: e.target.value })
                    }
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-medium text-sm text-slate-900 dark:text-white"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Institute Name
                  </label>
                  <input
                    type="text"
                    value={demoForm.institute}
                    onChange={(e) =>
                      setDemoForm({ ...demoForm, institute: e.target.value })
                    }
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-medium text-sm text-slate-900 dark:text-white"
                    placeholder="Your academy / school name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Message
                  </label>
                  <textarea
                    rows="2"
                    value={demoForm.message}
                    onChange={(e) =>
                      setDemoForm({ ...demoForm, message: e.target.value })
                    }
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-medium resize-none text-sm text-slate-900 dark:text-white"
                    placeholder="Tell us about your requirements..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full mt-2 py-4 bg-gradient-to-r from-violet-600 to-blue-600 text-white font-black rounded-xl shadow-lg shadow-blue-500/20 hover:from-violet-700 hover:to-blue-700 transition-all"
                >
                  Request Demo
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
